/* TECHNOVARD PRO – Precision Light: hero, 3D depth and light, scroll reveals and parallax */
(function () {
  'use strict';

  var root = document.documentElement;
  /* Media queries are read live, so changing the OS motion setting applies without a reload. */
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  /* Same heuristic as Shopify Horizon: skip pointer effects on very low-end devices. */
  var lowPower = Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  /* Theme setting "3D, light and scroll effects" (layout/theme.liquid adds tv-fx-off when off). */
  var effectsOn = !root.classList.contains('tv-fx-off');
  var heroes = new Set();

  function designMode() {
    return root.classList.contains('shopify-design-mode');
  }

  function pointerFx() {
    return effectsOn && finePointer.matches && !reducedMotion.matches && !lowPower;
  }

  function scrollFx() {
    return effectsOn && !reducedMotion.matches && !lowPower && !designMode();
  }

  /*
   * Follows the pointer over one element at a time. The element is measured once when tracking
   * starts (and again only after a scroll or resize), and pointer events are coalesced so
   * onFrame runs at most once per animation frame, with x and y between -0.5 and 0.5.
   */
  function PointerTracker(onFrame) {
    this.onFrame = onFrame;
    this.el = null;
    this.rect = null;
    this.frame = 0;
    this.onMove = this.onMove.bind(this);
    this.tick = this.tick.bind(this);
    this.invalidate = () => { this.rect = null; };
  }

  PointerTracker.prototype.start = function (el, event) {
    this.stop();
    this.el = el;
    el.addEventListener('pointermove', this.onMove, { passive: true });
    window.addEventListener('scroll', this.invalidate, { passive: true });
    window.addEventListener('resize', this.invalidate, { passive: true });
    if (event) this.onMove(event);
  };

  PointerTracker.prototype.onMove = function (event) {
    this.x = event.clientX;
    this.y = event.clientY;
    if (!this.frame) this.frame = requestAnimationFrame(this.tick);
  };

  PointerTracker.prototype.tick = function () {
    this.frame = 0;
    if (!this.el) return;
    if (!this.rect) this.rect = this.el.getBoundingClientRect();
    var r = this.rect;
    if (!r.width || !r.height) return;
    var x = Math.min(Math.max((this.x - r.left) / r.width, 0), 1) - 0.5;
    var y = Math.min(Math.max((this.y - r.top) / r.height, 0), 1) - 0.5;
    this.onFrame(this.el, x, y);
  };

  PointerTracker.prototype.stop = function () {
    var el = this.el;
    if (!el) return null;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    el.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('scroll', this.invalidate);
    window.removeEventListener('resize', this.invalidate);
    this.el = null;
    this.rect = null;
    return el;
  };

  class TvHero extends HTMLElement {
    /*
     * The editor can move a section in the DOM (reorder), which disconnects and reconnects the
     * same element, so element listeners are bound once and only document-level ones re-bind.
     */
    connectedCallback() {
      heroes.add(this);
      if (!this.ready) {
        this.ready = true;
        this.setup();
      }
      if (this.slides.length < 2) return;
      document.addEventListener('visibilitychange', this.onVisibility);
      this.syncMotion();
    }

    setup() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.tv-hero__slide'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.tv-hero__dot'));
      this.index = 0;
      this.speed = parseInt(this.dataset.speed || '7000', 10);
      this.wantsAutoplay = this.dataset.autoplay === 'true';
      this.style.setProperty('--tv-speed', this.speed + 'ms');

      this.parallax = new PointerTracker((el, x, y) => {
        el.style.setProperty('--tv-mx', x.toFixed(3));
        el.style.setProperty('--tv-my', y.toFixed(3));
      });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !pointerFx()) return;
        this.classList.add('is-lit');
        this.parallax.start(this, event);
      });
      this.addEventListener('pointerleave', () => this.resetParallax());

      if (this.slides.length < 2) return;

      this.track = this.querySelector('.tv-hero__track');
      this.toggle = this.querySelector('[data-tv-toggle]');
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => this.toggleAutoplay());
      }

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
      var prev = this.querySelector('[data-tv-prev]');
      var next = this.querySelector('[data-tv-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true));

      /* Hover, keyboard focus, a hidden tab and the theme editor hold rotation temporarily. */
      this.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.pause();
      });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.resume();
      });
      this.addEventListener('focusin', () => {
        this.focusInside = true;
        this.pause();
      });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusInside = false;
        this.resume();
      });

      var startX = null;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
        startX = null;
      });

      this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      heroes.delete(this);
      this.parallax.stop();
      /* No mouseleave/focusout fires for a removed element, so clear the temporary holds. */
      this.hovered = false;
      this.focusInside = false;
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /*
     * Autoplay runs when enabled in the section settings, unless the shopper paused it with the
     * toggle. With reduced motion it stays off until the shopper explicitly presses play.
     */
    get autoplay() {
      if (!this.wantsAutoplay || this.stopped) return false;
      return this.userStarted || !reducedMotion.matches;
    }

    toggleAutoplay() {
      if (this.autoplay) {
        this.stopped = true;
        this.userStarted = false;
        this.pause();
      } else {
        this.stopped = false;
        this.userStarted = true;
        this.play();
      }
      this.updateToggle();
    }

    updateToggle() {
      var rotating = this.autoplay;
      this.classList.toggle('is-stopped', !rotating);
      /* Announce slide changes only while rotation is stopped (WAI-ARIA carousel pattern). */
      if (this.track) this.track.setAttribute('aria-live', rotating ? 'off' : 'polite');
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', rotating ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
      }
    }

    resetParallax() {
      this.parallax.stop();
      this.classList.remove('is-lit');
      this.style.setProperty('--tv-mx', '0');
      this.style.setProperty('--tv-my', '0');
    }

    syncMotion() {
      if (!pointerFx()) this.resetParallax();
      this.updateToggle();
      if (this.autoplay) this.resume();
      else this.pause();
    }

    restartDot() {
      var dot = this.dots[this.index];
      if (!dot) return;
      dot.classList.remove('is-active');
      void dot.offsetWidth;
      dot.classList.add('is-active');
    }

    go(target, fromUser) {
      var n = this.slides.length;
      var i = ((target % n) + n) % n;
      if (i === this.index) return;
      var current = this.slides[this.index];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      if (this.dots[this.index]) {
        this.dots[this.index].classList.remove('is-active');
        this.dots[this.index].setAttribute('aria-current', 'false');
      }
      this.index = i;
      this.slides[i].classList.add('is-active');
      this.slides[i].removeAttribute('aria-hidden');
      if (this.dots[i]) this.dots[i].setAttribute('aria-current', 'true');
      this.restartDot();
      if (fromUser && !this.classList.contains('is-paused')) this.play();
    }

    play() {
      if (!this.autoplay || this.slides.length < 2) return;
      this.classList.remove('is-paused');
      clearInterval(this.timer);
      this.restartDot();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    pause() {
      this.classList.add('is-paused');
      clearInterval(this.timer);
    }

    /* Restarts rotation unless something is temporarily holding it. */
    resume() {
      if (this.hovered || this.focusInside || this.editorHold || document.hidden) return;
      this.play();
    }
  }

  if (!customElements.get('tv-hero')) customElements.define('tv-hero', TvHero);

  /* Theme editor: selecting a slide block shows that slide and holds rotation until deselected. */
  function heroForBlock(event) {
    var hero = event.target.closest ? event.target.closest('tv-hero') : null;
    return hero && hero.slides && hero.slides.indexOf(event.target) !== -1 ? hero : null;
  }

  document.addEventListener('shopify:block:select', function (event) {
    var hero = heroForBlock(event);
    if (!hero) return;
    hero.editorHold = true;
    hero.pause();
    hero.go(hero.slides.indexOf(event.target));
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var hero = heroForBlock(event);
    if (!hero) return;
    hero.editorHold = false;
    hero.resume();
  });

  /*
   * TvDepth: 3D tilt, layered depth, specular light and dynamic shadow for TECHNOVARD tiles and
   * for Dawn and Vitals product cards (desktop pointer only, not in the theme editor). JS only
   * writes --tv-px/--tv-py (-0.5..0.5); tilt, layers, light and shadow are computed in
   * technovard.css. The listener is delegated, so sections re-rendered by the editor, by filters
   * or by Vitals need no setup.
   */
  var DEPTH_SELECTOR = '[data-tv-tilt], .card-wrapper.product-card-wrapper, .VtlsProductsGridProdCard';
  /* Never tilt a card that holds a product form or a modal (Dawn quick add): a transformed
     ancestor would trap position:fixed UI. Nor anything inside purchase or overlay UI. */
  var DEPTH_EXCLUDE_INSIDE = 'form, product-form, quick-add-modal, quick-add-bulk, modal-opener, details-modal';
  var DEPTH_EXCLUDE_AROUND = 'cart-drawer, cart-notification, predictive-search, .quick-add-modal, .product__info-wrapper, .product-media-modal, .shopify-section-group-header-group, .shopify-section-group-footer-group';
  var depthAllowed = new WeakMap();

  function depthTarget(node) {
    var el = node.closest ? node.closest(DEPTH_SELECTOR) : null;
    if (!el) return null;
    if (!depthAllowed.has(el)) {
      depthAllowed.set(el, !el.querySelector(DEPTH_EXCLUDE_INSIDE) && !el.closest(DEPTH_EXCLUDE_AROUND));
    }
    return depthAllowed.get(el) ? el : null;
  }

  var depth = new PointerTracker(function (el, x, y) {
    el.style.setProperty('--tv-px', x.toFixed(3));
    el.style.setProperty('--tv-py', y.toFixed(3));
  });

  function releaseDepth() {
    var el = depth.stop();
    if (!el) return;
    el.removeEventListener('pointerleave', releaseDepth);
    el.classList.remove('is-tilting');
  }

  function engageDepth(el, event) {
    releaseDepth();
    el.classList.add('is-tilting');
    el.addEventListener('pointerleave', releaseDepth);
    depth.start(el, event);
  }

  /*
   * Pointer stages: [data-tv-pointer] sections get --tv-mx/--tv-my (-0.5..0.5) and .is-lit while
   * a fine pointer is over them; their light and parallax layers are positioned in CSS.
   */
  var stage = new PointerTracker(function (el, x, y) {
    el.style.setProperty('--tv-mx', x.toFixed(3));
    el.style.setProperty('--tv-my', y.toFixed(3));
  });

  function releaseStage() {
    var el = stage.stop();
    if (!el) return;
    el.removeEventListener('pointerleave', releaseStage);
    el.classList.remove('is-lit');
    el.style.setProperty('--tv-mx', '0');
    el.style.setProperty('--tv-my', '0');
  }

  function engageStage(el, event) {
    releaseStage();
    el.classList.add('is-lit');
    el.addEventListener('pointerleave', releaseStage);
    stage.start(el, event);
  }

  /* Magnetic buttons: [data-tv-magnet] leans a few pixels toward the pointer (CSS decides how far). */
  var magnet = new PointerTracker(function (el, x, y) {
    el.style.setProperty('--tv-bx', x.toFixed(3));
    el.style.setProperty('--tv-by', y.toFixed(3));
  });

  function releaseMagnet() {
    var el = magnet.stop();
    if (!el) return;
    el.removeEventListener('pointerleave', releaseMagnet);
    el.classList.remove('is-magnet');
  }

  function engageMagnet(el, event) {
    releaseMagnet();
    el.classList.add('is-magnet');
    el.addEventListener('pointerleave', releaseMagnet);
    magnet.start(el, event);
  }

  if (!lowPower) {
    document.addEventListener('pointerover', function (event) {
      if (event.pointerType === 'touch' || !pointerFx() || designMode()) return;
      var target = event.target;
      if (!target.closest) return;
      var el = depthTarget(target);
      if (el && el !== depth.el) engageDepth(el, event);
      var lit = target.closest('[data-tv-pointer]');
      if (lit && lit !== stage.el) engageStage(lit, event);
      var mag = target.closest('[data-tv-magnet]');
      if (mag && mag !== magnet.el) engageMagnet(mag, event);
    }, { passive: true });
  }

  /*
   * Category index: the row under the pointer or keyboard focus brings its image into the
   * viewer. Rows are plain links, so touch and no-JS simply navigate.
   */
  function activateIndexRow(row) {
    var index = row.closest('[data-tv-index]');
    if (!index || row.classList.contains('is-active')) return;
    var i = row.getAttribute('data-tv-index-row');
    index.querySelectorAll('[data-tv-index-row]').forEach((r) => r.classList.toggle('is-active', r === row));
    var slides = Array.from(index.querySelectorAll('[data-tv-index-slide]'));
    slides.forEach((s, n) => {
      var active = s.getAttribute('data-tv-index-slide') === i;
      s.classList.toggle('is-active', active);
      // the viewer's pictures load on demand: this one and the next two, so moving down the list stays instant
      if (active) slides.slice(n, n + 3).forEach(loadIndexSlide);
    });
  }

  function loadIndexSlide(slide) {
    var tpl = slide.querySelector('template[data-tv-index-img]');
    if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
  }

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType === 'touch' || !event.target.closest) return;
    var row = event.target.closest('[data-tv-index-row]');
    if (row) activateIndexRow(row);
  }, { passive: true });

  document.addEventListener('focusin', function (event) {
    var row = event.target.closest ? event.target.closest('[data-tv-index-row]') : null;
    if (row) activateIndexRow(row);
  });

  /*
   * Product rails: collection tabs (WAI-ARIA tabs with arrow keys), previous/next buttons and a
   * progress line for horizontally scrolling tracks. Scroll work is batched per frame.
   */
  var rails = {
    init(scope) {
      scope.querySelectorAll('[data-tv-rail]').forEach((rail) => this.setup(rail));
    },

    setup(rail) {
      if (rail.tvRail) return;
      rail.tvRail = true;
      rail.addEventListener('click', (event) => {
        var tab = event.target.closest('[data-tv-tab]');
        if (tab) return this.select(rail, tab);
        var arrow = event.target.closest('[data-tv-rail-dir]');
        if (arrow) this.step(rail, Number(arrow.getAttribute('data-tv-rail-dir')));
      });
      rail.addEventListener('keydown', (event) => {
        var tab = event.target.closest('[data-tv-tab]');
        if (!tab || ['ArrowRight', 'ArrowLeft', 'Home', 'End'].indexOf(event.key) === -1) return;
        var tabs = Array.prototype.slice.call(rail.querySelectorAll('[data-tv-tab]'));
        var i = tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
        if (event.key === 'Home') i = 0;
        if (event.key === 'End') i = tabs.length - 1;
        var next = tabs[(i + tabs.length) % tabs.length];
        event.preventDefault();
        next.focus();
        this.select(rail, next);
      });
      rail.querySelectorAll('.tv-rail__track').forEach((track) => {
        track.addEventListener('scroll', () => this.schedule(rail), { passive: true });
      });
      var onResize = () => {
        if (!rail.isConnected) return window.removeEventListener('resize', onResize);
        this.schedule(rail);
      };
      window.addEventListener('resize', onResize, { passive: true });
      this.update(rail);
    },

    track(rail) {
      return rail.querySelector('.tv-rail__panel:not([hidden]) .tv-rail__track');
    },

    select(rail, tab) {
      rail.querySelectorAll('[data-tv-tab]').forEach((t) => {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      var track = this.track(rail);
      if (track) track.scrollLeft = 0;
      this.update(rail);
    },

    step(rail, dir) {
      var track = this.track(rail);
      if (!track) return;
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    },

    schedule(rail) {
      if (rail.tvFrame) return;
      rail.tvFrame = requestAnimationFrame(() => {
        rail.tvFrame = 0;
        this.update(rail);
      });
    },

    update(rail) {
      var track = this.track(rail);
      if (!track) return;
      var max = track.scrollWidth - track.clientWidth;
      var progress = max > 0 ? track.scrollLeft / max : 0;
      var visible = track.scrollWidth ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
      rail.style.setProperty('--tv-rail-p', progress.toFixed(3));
      rail.style.setProperty('--tv-rail-w', visible.toFixed(3));
      rail.querySelectorAll('[data-tv-rail-dir]').forEach((button) => {
        var back = Number(button.getAttribute('data-tv-rail-dir')) < 0;
        button.disabled = back ? track.scrollLeft <= 2 : track.scrollLeft >= max - 2;
      });
    },
  };

  /*
   * Story: on desktop the chapter in the middle of the viewport drives the sticky image frame.
   * .is-ready (added here) is what dims inactive chapters, so without JS all text stays full.
   */
  var story = {
    init(scope) {
      if (!('IntersectionObserver' in window)) return;
      scope.querySelectorAll('[data-tv-story]').forEach((root) => {
        if (root.tvStory) return;
        root.tvStory = true;
        root.classList.add('is-ready');
        var observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate(root, entry.target.getAttribute('data-tv-story-step'));
          });
        }, { rootMargin: '-45% 0px -45% 0px' });
        root.tvStoryObserver = observer;
        root.querySelectorAll('[data-tv-story-step]').forEach((step) => observer.observe(step));
      });
    },

    activate(root, i) {
      root.querySelectorAll('[data-tv-story-step]').forEach((s) => s.classList.toggle('is-active', s.getAttribute('data-tv-story-step') === i));
      root.querySelectorAll('[data-tv-story-shot]').forEach((s) => s.classList.toggle('is-active', s.getAttribute('data-tv-story-shot') === i));
      var current = root.querySelector('.tv-story__current');
      if (current) current.textContent = (Number(i) + 1 < 10 ? '0' : '') + (Number(i) + 1);
      root.style.setProperty('--tv-story-i', i);
    },
  };

  /*
   * Scroll reveals: items fade and rise once, staggered within their group, when the group enters
   * the viewport. Groups are TECHNOVARD sections (data-tv-reveal-group / data-tv-reveal) and
   * Vitals product grids. The hidden state only applies under html.tv-reveal, which is added
   * here, so without JS, with reduced motion or in the theme editor everything stays visible.
   * Follows Dawn's "Reveal sections on scroll" setting (html.tv-reveal-enabled).
   */
  var REVEAL_GROUPS = [
    ['[data-tv-reveal-group]', '[data-tv-reveal]'],
    ['.VtlsFeaturedCollectionGridContent__Products', '.VtlsProductsGridProdCard'],
  ];

  var reveal = {
    init() {
      if (!root.classList.contains('tv-reveal-enabled') || !('IntersectionObserver' in window)) return;
      if (reducedMotion.matches || designMode()) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          this.observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      root.classList.add('tv-reveal');
      this.prepare(document);
      document.addEventListener('shopify:section:load', (event) => this.prepare(event.target));
    },

    prepare(scope) {
      REVEAL_GROUPS.forEach((pair) => {
        scope.querySelectorAll(pair[0]).forEach((group) => {
          if (group.hasAttribute('data-tv-reveal-ready')) return;
          group.setAttribute('data-tv-reveal-ready', '');
          group.querySelectorAll(pair[1]).forEach((item, i) => {
            item.setAttribute('data-tv-reveal-item', '');
            item.style.setProperty('--tv-i', Math.min(i, 8));
          });
          this.observer.observe(group);
        });
      });
    },
  };

  /*
   * Scroll depth: elements with data-tv-parallax get --tv-sp, from -1 (entering at the bottom)
   * through 0 (centred) to 1 (leaving at the top); CSS moves their layers from it. The measured
   * element itself is never moved (only its descendants), so there is no feedback loop. Only
   * elements near the viewport are measured, once per frame, and only while scrolling.
   */
  var scrollDepth = {
    visible: new Set(),
    frame: 0,
    listening: false,

    init() {
      if (!effectsOn || lowPower || !('IntersectionObserver' in window)) return;
      this.update = this.update.bind(this);
      this.schedule = this.schedule.bind(this);
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        });
        this.listen(this.visible.size > 0);
        this.schedule();
      }, { rootMargin: '25% 0px' });
      this.observe(document);
      document.addEventListener('shopify:section:load', (event) => this.observe(event.target));
    },

    observe(scope) {
      scope.querySelectorAll('[data-tv-parallax]').forEach((el) => this.observer.observe(el));
    },

    listen(on) {
      if (on === this.listening) return;
      this.listening = on;
      var method = on ? 'addEventListener' : 'removeEventListener';
      window[method]('scroll', this.schedule, { passive: true });
      window[method]('resize', this.schedule, { passive: true });
    },

    schedule() {
      if (!this.frame && this.visible.size) this.frame = requestAnimationFrame(this.update);
    },

    update() {
      this.frame = 0;
      var vh = window.innerHeight;
      var on = scrollFx();
      var reads = [];
      this.visible.forEach((el) => reads.push([el, on ? el.getBoundingClientRect() : null]));
      reads.forEach((item) => {
        var r = item[1];
        var p = r ? (vh / 2 - (r.top + r.height / 2)) / ((vh + r.height) / 2) : 0;
        item[0].style.setProperty('--tv-sp', Math.max(-1, Math.min(1, p)).toFixed(3));
      });
    },

    /* Called when the motion preference changes: recompute (or zero) every visible element. */
    refresh() {
      if (this.observer) this.schedule();
    },
  };

  /*
   * Ambient motion only while it can be seen: elements with data-tv-live get .is-offscreen when
   * they leave the viewport, and their CSS animations pause (smart room, page headers). One observer.
   */
  var live = {
    init(scope) {
      if (!('IntersectionObserver' in window)) return;
      if (!this.observer) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => entry.target.classList.toggle('is-offscreen', !entry.isIntersecting));
        }, { rootMargin: '80px 0px' });
      }
      scope.querySelectorAll('[data-tv-live]').forEach((el) => this.observer.observe(el));
    },
  };

  /*
   * TECHNOVARD emblem in 3D ([data-tv-spin] around snippets/tv-emblem-3d.liquid: footer logo,
   * About page). It turns once around its vertical axis when the pointer arrives, when a link
   * inside gets keyboard focus, on a tap, and once when it first scrolls into view on touch
   * screens; it leans toward the pointer and can
   * be turned by dragging sideways (vertical scrolling is left to the browser: touch-action
   * pan-y). Only CSS variables on one element change, from a frame loop that stops when still.
   * Nothing moves with reduced motion or when the 3D effects setting is off.
   */
  var SPIN_MS = 1400;
  var easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  function EmblemSpin(el) {
    this.el = el;
    this.emblem = el.querySelector('[data-tv-spin-handle]');
    this.rot = el.querySelector('.tv-emblem3d__rot');
    this.turn = 0; // own rotation (spin, drag, inertia), degrees
    this.tiltX = 0;
    this.tiltY = 0;
    this.goalX = 0;
    this.goalY = 0;
    this.spinStart = 0;
    this.spinFrom = 0;
    this.velocity = 0;
    this.drag = null;
    this.frame = 0;
    this.tick = this.tick.bind(this);
    el.addEventListener('pointerenter', (event) => {
      this.rect = null;
      if (event.pointerType === 'mouse') this.spinOnce();
    });
    el.addEventListener('pointermove', (event) => this.lean(event), { passive: true });
    el.addEventListener('pointerleave', () => {
      this.goalX = 0;
      this.goalY = 0;
      this.run();
    });
    el.addEventListener('focusin', (event) => {
      if (event.target.matches(':focus-visible')) this.spinOnce();
    });
    this.emblem.addEventListener('pointerdown', (event) => this.down(event));
    this.emblem.addEventListener('pointermove', (event) => this.move(event));
    this.emblem.addEventListener('pointerup', (event) => this.up(event));
    this.emblem.addEventListener('pointercancel', (event) => this.up(event, true));
    this.emblem.addEventListener('dragstart', (event) => event.preventDefault());
    if (!finePointer.matches && 'IntersectionObserver' in window) {
      var seen = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        seen.disconnect();
        setTimeout(() => this.spinOnce(), 300);
      }, { threshold: 0.8 });
      seen.observe(el);
    }
  }

  EmblemSpin.prototype.allowed = function () {
    return effectsOn && !reducedMotion.matches && !designMode();
  };

  EmblemSpin.prototype.spinOnce = function () {
    if (!this.allowed() || this.spinStart || this.drag) return;
    this.velocity = 0;
    this.turn %= 360;
    this.spinFrom = this.turn;
    this.spinStart = window.performance.now();
    this.run();
  };

  EmblemSpin.prototype.lean = function (event) {
    if (!this.allowed() || event.pointerType !== 'mouse') return;
    if (!this.rect) this.rect = this.emblem.getBoundingClientRect();
    var x = (event.clientX - this.rect.left) / this.rect.width - 0.5;
    var y = (event.clientY - this.rect.top) / this.rect.height - 0.5;
    this.goalY = Math.max(-1, Math.min(1, x)) * 24;
    this.goalX = Math.max(-1, Math.min(1, y)) * -16;
    this.run();
  };

  EmblemSpin.prototype.down = function (event) {
    if (!this.allowed() || event.button > 0) return;
    this.drag = { x: event.clientX, turn: this.turn, last: event.clientX, time: event.timeStamp, moved: false, id: event.pointerId };
    this.spinStart = 0;
    this.velocity = 0;
  };

  EmblemSpin.prototype.move = function (event) {
    var d = this.drag;
    if (!d || event.pointerId !== d.id) return;
    var dx = event.clientX - d.x;
    if (!d.moved && Math.abs(dx) > 6) {
      d.moved = true;
      this.el.classList.add('is-dragging');
      this.emblem.setPointerCapture(d.id);
    }
    if (!d.moved) return;
    var dt = Math.max(1, event.timeStamp - d.time);
    this.velocity = ((event.clientX - d.last) / dt) * 16 * 0.9;
    d.last = event.clientX;
    d.time = event.timeStamp;
    this.turn = d.turn + dx * 0.9;
    this.run();
  };

  EmblemSpin.prototype.up = function (event, cancelled) {
    var d = this.drag;
    if (!d || event.pointerId !== d.id) return;
    this.drag = null;
    this.el.classList.remove('is-dragging');
    if (!d.moved && !cancelled) {
      this.spinOnce();
      return;
    }
    this.run();
  };

  EmblemSpin.prototype.run = function () {
    this.el.classList.add('is-active');
    if (!this.frame) this.frame = requestAnimationFrame(this.tick);
  };

  EmblemSpin.prototype.tick = function (now) {
    this.frame = 0;
    if (!this.allowed()) {
      this.reset();
      return;
    }
    var moving = false;
    if (this.spinStart) {
      var t = Math.min(1, (now - this.spinStart) / SPIN_MS);
      this.turn = this.spinFrom + 360 * easeInOut(t);
      if (t >= 1) {
        this.spinStart = 0;
        this.turn = this.spinFrom;
      }
      moving = true;
    } else if (!this.drag) {
      if (Math.abs(this.velocity) > 0.15) {
        /* inertia after a drag */
        this.turn += this.velocity;
        this.velocity *= 0.94;
        moving = true;
      } else {
        /* settle on the nearest full turn, facing front */
        this.velocity = 0;
        var target = Math.round(this.turn / 360) * 360;
        var gap = target - this.turn;
        this.turn = Math.abs(gap) < 0.2 ? target : this.turn + gap * 0.12;
        moving = Math.abs(gap) >= 0.2;
      }
    } else {
      moving = true;
    }
    this.tiltX += (this.goalX - this.tiltX) * 0.12;
    this.tiltY += (this.goalY - this.tiltY) * 0.12;
    if (Math.abs(this.goalX - this.tiltX) > 0.05 || Math.abs(this.goalY - this.tiltY) > 0.05) moving = true;
    var ry = this.turn + this.tiltY;
    var glint = ((((ry + 60) % 360) + 360) % 360) / 360;
    this.rot.style.setProperty('--rx', this.tiltX.toFixed(2) + 'deg');
    this.rot.style.setProperty('--ry', ry.toFixed(2) + 'deg');
    this.rot.style.setProperty('--tv-glint', glint.toFixed(3));
    if (moving) {
      this.frame = requestAnimationFrame(this.tick);
    } else {
      this.turn %= 360;
      this.rect = null;
      if (!this.el.matches(':hover')) this.el.classList.remove('is-active');
    }
  };

  EmblemSpin.prototype.reset = function () {
    this.spinStart = 0;
    this.velocity = 0;
    this.drag = null;
    this.turn = this.tiltX = this.tiltY = this.goalX = this.goalY = 0;
    this.rot.style.removeProperty('--rx');
    this.rot.style.removeProperty('--ry');
    this.rot.style.removeProperty('--tv-glint');
    this.el.classList.remove('is-active', 'is-dragging');
  };

  var spin = {
    init(scope) {
      scope.querySelectorAll('[data-tv-spin]').forEach((el) => {
        if (!el.tvSpin && el.querySelector('[data-tv-spin-handle]') && el.querySelector('.tv-emblem3d__rot')) el.tvSpin = new EmblemSpin(el);
      });
    },
  };

  /* Collection header: bring the current category's chip into view inside its strip (only the
     strip scrolls, never the page). */
  var chips = {
    init(scope) {
      scope.querySelectorAll('.tv-chero__chips').forEach((strip) => {
        var current = strip.querySelector('.is-current');
        if (!current || strip.scrollWidth <= strip.clientWidth) return;
        var item = current.parentElement;
        // position inside the strip's scrolled content (offsetLeft would be relative to the outer nav)
        var x = item.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        var left = x - (strip.clientWidth - item.offsetWidth) / 2;
        strip.scrollLeft = Math.max(0, Math.min(left, strip.scrollWidth - strip.clientWidth));
      });
    },
  };

  /*
   * Panorama hero ([data-tv-pano], sections/tv-stage.liquid). The depth bands follow the
   * pointer in CSS (--tv-ex from --tv-mx); this adds the "Explore in layers" button (touch and
   * keyboard), starts the sideways panorama on phones at the chosen point, and runs the guided
   * highlight: one hotspot at a time shows its label, every few seconds, only while the hero is
   * on screen and nobody is pointing at or focusing a hotspot. No highlight with reduced motion.
   */
  var TOUR_MS = 2800;

  function Panorama(el) {
    this.el = el;
    this.track = el.querySelector('.tv-pano__track');
    this.hots = Array.prototype.slice.call(el.querySelectorAll('.tv-hot'));
    var toggle = el.querySelector('[data-tv-pano-toggle]');
    if (toggle) {
      toggle.addEventListener('click', () => {
        var open = !el.classList.contains('is-open');
        el.classList.toggle('is-open', open);
        toggle.setAttribute('aria-pressed', String(open));
      });
    }
    this.center();
    if (el.hasAttribute('data-tv-tour')) this.startTour();
  }

  Panorama.prototype.center = function () {
    var track = this.track;
    if (!track || track.scrollWidth <= track.clientWidth) return;
    var focus = parseFloat(getComputedStyle(this.el).getPropertyValue('--focus'));
    if (isNaN(focus)) focus = 50;
    track.scrollLeft = ((track.scrollWidth - track.clientWidth) * focus) / 100;
  };

  Panorama.prototype.clearSpot = function () {
    this.hots.forEach((hot) => hot.classList.remove('is-spot'));
  };

  Panorama.prototype.startTour = function () {
    var index = -1;
    var paused = false;
    var stage = this.el.closest('[data-tv-live]');
    var step = () => {
      if (!this.el.isConnected) return;
      if (!effectsOn || reducedMotion.matches || designMode()) {
        this.clearSpot();
      } else if (!paused && !(stage && stage.classList.contains('is-offscreen'))) {
        var shown = this.hots.filter((hot) => hot.offsetParent !== null);
        this.clearSpot();
        if (shown.length) {
          index = (index + 1) % shown.length;
          shown[index].classList.add('is-spot');
        }
      }
      setTimeout(step, TOUR_MS);
    };
    var pause = () => {
      paused = true;
      this.clearSpot();
    };
    var resume = () => {
      paused = false;
    };
    this.hots.forEach((hot) => {
      hot.addEventListener('pointerenter', pause);
      hot.addEventListener('pointerleave', resume);
      hot.addEventListener('focus', pause);
      hot.addEventListener('blur', resume);
    });
    setTimeout(step, 2400);
  };

  var pano = {
    init(scope) {
      scope.querySelectorAll('[data-tv-pano]').forEach((el) => {
        if (!el.tvPano) el.tvPano = new Panorama(el);
      });
    },
  };

  reveal.init();
  scrollDepth.init();
  rails.init(document);
  story.init(document);
  live.init(document);
  spin.init(document);
  chips.init(document);
  pano.init(document);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-tv-story]').forEach((root) => {
      if (root.tvStoryObserver) root.tvStoryObserver.disconnect();
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    var panel = event.target.closest ? event.target.closest('.tv-rail__panel') : null;
    var rail = panel && panel.closest('[data-tv-rail]');
    var tab = rail && rail.querySelector('[data-tv-tab][aria-controls="' + panel.id + '"]');
    if (tab) rails.select(rail, tab);
  });
  document.addEventListener('shopify:section:load', (event) => {
    rails.init(event.target);
    story.init(event.target);
    live.init(event.target);
    spin.init(event.target);
    chips.init(event.target);
    pano.init(event.target);
  });

  function onMotionPreferenceChange() {
    releaseDepth();
    releaseStage();
    releaseMagnet();
    heroes.forEach((hero) => {
      hero.userStarted = false;
      hero.syncMotion();
    });
    scrollDepth.refresh();
    document.querySelectorAll('[data-tv-spin]').forEach((el) => el.tvSpin && el.tvSpin.reset());
  }

  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionPreferenceChange);
  else if (reducedMotion.addListener) reducedMotion.addListener(onMotionPreferenceChange);
})();